import React from 'react';
import { Search, LayoutGrid, Table } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export type StatusFilter = 'all' | 'active' | 'paused';
export type ViewLayout = 'grid' | 'table';

export interface WatchlistControlsProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (filter: StatusFilter) => void;
  viewLayout: ViewLayout;
  onViewLayoutChange: (layout: ViewLayout) => void;
  counts: {
    all: number;
    active: number;
    paused: number;
  };
}

export const WatchlistControls: React.FC<WatchlistControlsProps> = ({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  viewLayout,
  onViewLayoutChange,
  counts,
}) => {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
      {/* Search Input */}
      <div className="relative w-full sm:w-72">
        <Search className="w-4 h-4 text-slate-400 absolute start-3 top-2.5" />
        <input
          type="text"
          placeholder={t('dashboard.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full ps-9 pe-3.5 py-1.5 text-xs rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      {/* Filter Tabs & Layout Switcher */}
      <div className="flex items-center justify-between w-full sm:w-auto gap-3">
        {/* Status Filter Tabs */}
        <div className="inline-flex items-center p-1 rounded-xl bg-slate-100 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300">
          <button
            onClick={() => onStatusFilterChange('all')}
            className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
              statusFilter === 'all'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs font-semibold'
                : 'hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            {t('dashboard.filterAll')} ({counts.all})
          </button>
          <button
            onClick={() => onStatusFilterChange('active')}
            className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
              statusFilter === 'active'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs font-semibold'
                : 'hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            {t('dashboard.filterActive')} ({counts.active})
          </button>
          <button
            onClick={() => onStatusFilterChange('paused')}
            className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
              statusFilter === 'paused'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs font-semibold'
                : 'hover:text-slate-900 dark:hover:text-slate-100'
            }`}
          >
            {t('dashboard.filterPaused')} ({counts.paused})
          </button>
        </div>

        {/* Grid / Table Layout Toggle */}
        <div className="inline-flex items-center p-1 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
          <button
            onClick={() => onViewLayoutChange('grid')}
            className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
              viewLayout === 'grid'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs'
                : 'hover:text-slate-900 dark:hover:text-slate-100'
            }`}
            title="Grid View"
            aria-label="Grid View"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewLayoutChange('table')}
            className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
              viewLayout === 'table'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs'
                : 'hover:text-slate-900 dark:hover:text-slate-100'
            }`}
            title="Table View"
            aria-label="Table View"
          >
            <Table className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
